import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Badge, Button, Modal } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { cleanText } from "../../../utils/QuestionUtils";

export default function SurveyValidationDialog({ result, templateJson, isAdminUser = false, onHide }) {
  const intl = useIntl();
  const reportRef = useRef(null);
  const [popupBlocked, setPopupBlocked] = useState(false);
  const message = (id, values) => intl.formatMessage({ id: `survey.editor.validation.${id}` }, values);
  const errors = result.issues.filter((issue) => issue.severity === "error").length;
  const warnings = result.issues.length - errors;
  const openReportWindow = () => {
    const reportWindow = window.open("", "_blank", "width=1200,height=900,scrollbars=yes,resizable=yes");
    if (!reportWindow) {
      setPopupBlocked(true);
      return;
    }
    setPopupBlocked(false);
    const reportDocument = reportWindow.document;
    reportDocument.title = message("title");
    reportDocument.documentElement.lang = intl.locale;
    const base = reportDocument.createElement("base");
    base.href = document.baseURI;
    reportDocument.head.appendChild(base);
    const viewport = reportDocument.createElement("meta");
    viewport.name = "viewport";
    viewport.content = "width=device-width, initial-scale=1";
    reportDocument.head.appendChild(viewport);
    document.querySelectorAll('link[rel="stylesheet"], style').forEach((style) => {
      reportDocument.head.appendChild(style.cloneNode(true));
    });
    const title = reportDocument.createElement("h1");
    title.className = "h3 mb-4";
    title.textContent = message("title");
    reportDocument.body.style.cssText = "display: block; margin: 0; padding: 24px; overflow: auto;";
    // Copy the rendered report so it stays available after the editor dialog is closed.
    reportDocument.body.replaceChildren(title, reportRef.current.cloneNode(true));
    reportWindow.opener = null;
    reportWindow.focus();
  };
  const downloadJson = () => {
    if (!isAdminUser) return;
    const url = URL.createObjectURL(new Blob([templateJson], { type: "application/json;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = "survey-template.json";
    document.body.appendChild(link);
    try {
      link.click();
    } finally {
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    }
  };
  const locationLabel = (location) => {
    const { model, parent, pageIndex, questionIndex, triggerIndex, elementPath = [] } = location;
    const title = cleanText(model.title || model.name || "");
    const pageTitle = parent ? cleanText(parent.model.title || parent.model.name || "") : title;
    return (
      `${message("page")} ${pageIndex + 1}${pageTitle ? `: ${pageTitle}` : ""}` +
      `${questionIndex !== undefined ? ` / ${message("question")} ${questionIndex + 1}${title ? `: ${title}` : ""}` : ""}` +
      `${model.key ? ` [ID: ${model.key}]` : ""}` +
      elementPath
        .map((element) => {
          const label = cleanText(
            element.model.text || element.model.title || element.model.name || element.model.value || ""
          );
          return ` / ${message(element.type)} ${element.index + 1}${label ? `: ${label}` : ""}`;
        })
        .join("") +
      `${triggerIndex !== undefined ? ` / Trigger ${triggerIndex + 1}` : ""}`
    );
  };
  return (
    <Modal show onHide={onHide} size="xl" scrollable aria-labelledby="survey-validation-title">
      <Modal.Header closeButton>
        <Modal.Title id="survey-validation-title">{message("title")}</Modal.Title>
      </Modal.Header>
      <Modal.Body ref={reportRef}>
        <p>{message("scope", result)}</p>
        <Alert variant={errors ? "danger" : warnings ? "warning" : "success"}>
          {result.issues.length ? message("summary", { errors, warnings }) : message("success")}
        </Alert>
        {["ids", "triggers"].map((category) => {
          const issues = result.issues.filter((issue) => issue.category === category);
          return (
            <section key={category} className="mb-4" aria-labelledby={`validation-${category}`}>
              <h5 id={`validation-${category}`}>
                {message(category)} <Badge bg="secondary">{issues.length}</Badge>
              </h5>
              {!issues.length && <p className="text-muted">{message("noIssues")}</p>}
              {["error", "warning"].map((severity) => {
                const entries = issues.filter((issue) => issue.severity === severity);
                return (
                  entries.length > 0 && (
                    <div key={severity}>
                      <h6 className={severity === "error" ? "text-danger" : "text-warning"}>{message(severity)}</h6>
                      <ul className="list-unstyled">
                        {entries.map((issue, index) => (
                          <li key={index} className="border rounded p-3 mb-2" style={{ overflowWrap: "anywhere" }}>
                            <strong>{message(issue.code, issue.values)}</strong>
                            <ul className="mb-0 mt-1">
                              {category === "triggers" ? (
                                <li>
                                  {locationLabel(issue.locations[0])}
                                  {issue.locations.length > 1 && (
                                    <ul className="mb-0 mt-1">
                                      {issue.locations.slice(1).map((location, locationIndex) => (
                                        <li key={locationIndex}>{locationLabel(location)}</li>
                                      ))}
                                    </ul>
                                  )}
                                </li>
                              ) : (
                                issue.locations.map((location, locationIndex) => (
                                  <li key={locationIndex}>{locationLabel(location)}</li>
                                ))
                              )}
                            </ul>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )
                );
              })}
            </section>
          );
        })}
      </Modal.Body>
      <Modal.Footer>
        {popupBlocked && (
          <Alert variant="warning" className="w-100">
            {message("popupBlocked")}
          </Alert>
        )}
        <Button variant="outline-secondary" onClick={openReportWindow}>
          {message("openWindow")}
        </Button>
        {isAdminUser && (
          <Button variant="outline-secondary" onClick={downloadJson}>
            {message("downloadJson")}
          </Button>
        )}
        <Button variant="secondary" onClick={onHide}>
          <FormattedMessage id="app.common.Close" />
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

SurveyValidationDialog.propTypes = {
  result: PropTypes.object.isRequired,
  templateJson: PropTypes.string.isRequired,
  isAdminUser: PropTypes.bool,
  onHide: PropTypes.func.isRequired
};
